/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import { MemoizedNodeRenderer } from "./NodeRenderer";
import EdgeRenderer from "./EdgeRenderer";
import Service from "../api/service";
import { animateViewBox } from "../utility/animateViewBox";
import { convertEnoviaToDiagram, processTasks } from "../utility/convertEnoviaToDiagram";
import { loadTeamProject, updateTeamTask, updateTeamMilestone } from "../utility/teamProjectService";
import { deliverablesCache } from "./DeliverablesDots";
import { exportDiagram } from "../utility/exportDiagram";
import { computeNodesBounds } from "../utility/flowHelpers";
import {
  buildGraph,
  computeLevels,
  assignPositions,
  assignTimelinePositions,
} from "../utility/flowHelpers";
import Minimap from "./MXMinimap";
import ScrollBars from "./ScrollBars";
import Loader from "./loader/Loader";
import TimelineHeaders from "./TimelineHeaders";
import ToastNotification, {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "./toasts/ToastNotification";
import { showErrorPopup } from "./toasts/MessagePopup";
import { closeAllDropdowns } from "../utility/dropdownState";
import { clearAssigneeCache } from "../utility/assigneeCache";
import {
  ZoomIn,
  ZoomOut,
  Refresh,
  Download,
  FitScreen,
  ViewWeek,
  UnfoldLess,
  Home,
} from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SchemaIcon from "@mui/icons-material/Schema";
import TimelineIcon from "@mui/icons-material/Timeline";
import PersonIcon from "@mui/icons-material/Person";
import DeliverableModal from "./DeliverableModal";
import html2canvas from "html2canvas";
import { computeHierarchyChain } from "../utility/hierarchyChainHelpers";
import requestQueue from "../utility/requestQueue";

/* CONFIG */
const MIN_VIEW_W = 700;
const MIN_VIEW_H = 550;
const BATCH_UPDATE_DELAY = 16;
const VIRTUALIZATION_THRESHOLD = 150;
// function filterHierarchyOverlap(edges) {
//   const nodesWithDependency = new Set(
//     edges.filter(e => e.type === "dependency").map(e => e.to)
//   );
//   return edges.filter(e =>
//     e.type !== "hierarchy" || !nodesWithDependency.has(e.to)
//   );
// }
/* ========== STATE REDUCER ========== */
function diagramReducer(state, action) {
  switch (action.type) {
    case "MERGE_DIAGRAM_DATA":
      const mergedNodes = [...state.nodes, ...action.payload.nodes];
      const mergedEdges = [...state.edges, ...action.payload.edges];
      const mergedNodeMap = { ...state.nodeMap, ...action.payload.nodeMap };
      return {
        ...state,
        nodes: mergedNodes,
        edges: mergedEdges,
        nodeMap: mergedNodeMap,
        version: state.version + 1,
      };
    case "SET_DIAGRAM_DATA":
      return {
        ...state,
        nodes: action.payload.nodes,
        edges: action.payload.edges,
        nodeMap: action.payload.nodeMap,
        edgeControls: action.payload.edgeControls,
        version: state.version + 1,
      };
    case "UPDATE_NODES":
      const newNodes = action.payload.nodes;
      const newNodeMap = Object.fromEntries(newNodes.map((n) => [n.id, n]));
      return {
        ...state,
        nodes: newNodes,
        nodeMap: newNodeMap,
        version: state.version + 1,
      };
    case "UPDATE_NODE":
      const { nodeId, newAttributes } = action.payload;
      const updatedNodes = state.nodes.map(n =>
        n.id === nodeId ? { ...n, ...newAttributes } : n
      );
      return {
        ...state,
        nodes: updatedNodes,
        nodeMap: {
          ...state.nodeMap,
          [nodeId]: { ...state.nodeMap[nodeId], ...newAttributes }
        },
        version: state.version + 1,
      };
    case "UPDATE_EDGE_CONTROL":
      return {
        ...state,
        edgeControls: {
          ...state.edgeControls,
          [action.payload.edgeIndex]: action.payload.control,
        },
      };
    case "INCREMENT_VERSION":
      return {
        ...state,
        version: state.version + 1,
      };
    default:
      return state;
  }
}

/* ========== DEBOUNCE UTILITY ========== */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/* ========== MEMOIZED COMPONENTS ========== */

const MemoizedEdgeRenderer = React.memo(
  EdgeRenderer,
  (prevProps, nextProps) => {
    if (
      prevProps.hoverEdgeIndex === nextProps.edgeIndex ||
      nextProps.hoverEdgeIndex === nextProps.edgeIndex ||
      prevProps.selectedIndex === nextProps.edgeIndex ||
      nextProps.selectedIndex === nextProps.edgeIndex
    ) {
      return false;
    }

    const edgeChanged =
      !prevProps.edge || !nextProps.edge
        ? true
        : prevProps.edge.from !== nextProps.edge.from ||
        prevProps.edge.to !== nextProps.edge.to ||
        prevProps.edge.type !== nextProps.edge.type ||
        prevProps.edge.isCritical !== nextProps.edge.isCritical;

    const nodeChanged =
      prevProps.nodeById[prevProps.edge?.from]?.x !==
      nextProps.nodeById[nextProps.edge?.from]?.x ||
      prevProps.nodeById[prevProps.edge?.from]?.y !==
      nextProps.nodeById[nextProps.edge?.from]?.y ||
      prevProps.nodeById[prevProps.edge?.to]?.x !==
      nextProps.nodeById[nextProps.edge?.to]?.x ||
      prevProps.nodeById[prevProps.edge?.to]?.y !==
      nextProps.nodeById[nextProps.edge?.to]?.y;

    return (
      !edgeChanged &&
      !nodeChanged &&
      prevProps.isInChain === nextProps.isInChain &&
      prevProps.opacity === nextProps.opacity &&
      prevProps.showCriticalPath === nextProps.showCriticalPath
    );
  }
);

export default function FlowDiagram({ data, onHome, onFatalError }) {
  /* ========== REDUCER STATE ========== */
  const [diagramState, dispatch] = useReducer(diagramReducer, {
    nodes: [],
    edges: [],
    nodeMap: {},
    edgeControls: {},
    version: 0,
  });

  /* ========== LOCAL STATE ========== */
  const [fullData, setFullData] = useState({
    nodes: [],
    edges: [],
    nodeById: {},
  });
  const [expanded, setExpanded] = useState(new Set());
  const expandedRef = useRef(expanded);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);
  const [activeDropdownNodeId, setActiveDropdownNodeId] = useState(null);

  const [expandDepth] = useState(999);
  const [preChainViewBox, setPreChainViewBox] = useState(null);
  const [preChainExpanded, setPreChainExpanded] = useState(new Set());
  const [exitedChainNodes, setExitedChainNodes] = useState(new Set());

  const [selectedEdgeIndex, setSelectedEdgeIndex] = useState(null);
  const [hoverEdgeIndex, setHoverEdgeIndex] = useState(null);
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);
  const [showDependencyEdges, setShowDependencyEdges] = useState(true);
  const [showSubTaskEdges, setShowSubTaskEdges] = useState(true);
  const [showEdgeMenu, setShowEdgeMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [expandingNode, setExpandingNode] = useState(false);
  const [promotingNode, setPromotingNode] = useState(false);
  const [activeInteraction, setActiveInteraction] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [assigneeTooltip, setAssigneeTooltip] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [forceResetHover, setForceResetHover] = useState(false);
  const [deliverableTask, setDeliverableTask] = useState(null); // { id, title }
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  // Tracks nodes we've already fetched but found no children (confirmed leaf nodes)
  const [fetchedEmptyNodes, setFetchedEmptyNodes] = useState(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [isProjectFullyLoaded, setIsProjectFullyLoaded] = useState(false);

  /* ========== TIMELINE STATE ========== */
  const [timelineMode, setTimelineMode] = useState(false);
  const [timelineSortBy, setTimelineSortBy] = useState('startDate');
  const [fitToScreenLoading, setFitToScreenLoading] = useState(false);
  const [isWaitingForDeliverables, setIsWaitingForDeliverables] = useState(false);

  /* ========== HIGHLIGHT CHAIN STATE ========== */
  const [highlightedChain, setHighlightedChain] = useState({
    nodeId: null,
    chainNodeIds: new Set(),
    chainEdgeIds: new Set(),
  });

  /* ========== VIEWBOX STATE ========== */
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 1200, h: 800 });
  const [zoomRatio, setZoomRatio] = useState(1);

  useEffect(() => {
    const updateZoom = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        if (rect.width > 0 && viewBox.w > 0) {
          setZoomRatio(rect.width / viewBox.w);
        }
      }
    };
    updateZoom();
    // Re-run on window resize as well
    window.addEventListener("resize", updateZoom);
    return () => window.removeEventListener("resize", updateZoom);
  }, [viewBox.w]);

  /* ========== REFS ========== */
  const svgRef = useRef(null);

  useEffect(() => {
    const handleIdle = () => {
      if (isWaitingForDeliverables) {
        setIsWaitingForDeliverables(false);
        showSuccessToast("All deliverables loaded");
      }
    };
    window.addEventListener("requestQueueIdle", handleIdle);
    return () => window.removeEventListener("requestQueueIdle", handleIdle);
  }, [isWaitingForDeliverables]);
  const containerRef = useRef(null);
  const initialDone = useRef(false);
  const lastProjectIdRef = useRef(null);

  const panningRef = useRef({
    active: false,
    startClient: { x: 0, y: 0 },
    startView: null,
  });

  const draggingRef = useRef({
    dragging: false,
    edgeIndex: null,
    offsetX: 0,
    offsetY: 0,
  });

  const nodeDragRef = useRef({
    active: false,
    nodeId: null,
    startClient: { x: 0, y: 0 },
    startNodePos: { x: 0, y: 0 },
  });

  /* ========== MEMOIZED VALUES ========== */
  const bounds = useMemo(() => {
    return computeNodesBounds(diagramState.nodes);
  }, [diagramState.nodes]);

  const allAssignees = useMemo(() => {
    const set = new Set();
    (fullData.nodes || []).forEach(n =>
      (n.assignees || []).forEach(a => { if (a) set.add(a); })
    );
    return [...set].sort();
  }, [fullData.nodes]);



  /* ========== VISIBLE ELEMENTS (VIRTUALIZATION) ========== */
const { visibleNodes, visibleEdges } = useMemo(() => {
  const diagramWidth = bounds.maxX - bounds.minX;
  const diagramHeight = bounds.maxY - bounds.minY;

  const VIEWPORT_PADDING = Math.max(
    0.1 * Math.max(diagramWidth, diagramHeight),
    50
  );

  // Check whether an edge type is currently enabled
  const isEdgeEnabled = (edge) => {
    if (edge.type === "dependency") {
      return showDependencyEdges;
    }

    if (edge.type === "hierarchy") {
      return showSubTaskEdges;
    }

    // Keep any other edge types visible
    return true;
  };

  /* ---------- Small diagrams ---------- */
  if (diagramState.nodes.length < VIRTUALIZATION_THRESHOLD) {
    return {
      visibleNodes: diagramState.nodes,
      visibleEdges: diagramState.edges.filter(isEdgeEnabled),
    };
  }

  /* ---------- Virtualized diagrams ---------- */
  const paddedViewBox = {
    x: viewBox.x - VIEWPORT_PADDING,
    y: viewBox.y - VIEWPORT_PADDING,
    w: viewBox.w + VIEWPORT_PADDING * 2,
    h: viewBox.h + VIEWPORT_PADDING * 2,
  };

  const visibleNodeIds = new Set();

  const visibleNodes = diagramState.nodes.filter((node) => {
    const nodeRight = node.x + (node.width || 220);
    const nodeBottom = node.y + (node.height || 85);

    const isVisible = !(
      nodeRight < paddedViewBox.x ||
      node.x > paddedViewBox.x + paddedViewBox.w ||
      nodeBottom < paddedViewBox.y ||
      node.y > paddedViewBox.y + paddedViewBox.h
    );

    if (isVisible) {
      visibleNodeIds.add(node.id);
    }

    return isVisible;
  });

  const visibleEdges = diagramState.edges.filter((edge) => {
    // First check whether this edge type is enabled
    if (!isEdgeEnabled(edge)) {
      return false;
    }

    const fromNode = diagramState.nodeMap[edge.from];
    const toNode = diagramState.nodeMap[edge.to];

    if (!fromNode || !toNode) {
      return false;
    }

    return (
      visibleNodeIds.has(edge.from) ||
      visibleNodeIds.has(edge.to)
    );
  });

  return {
    visibleNodes,
    visibleEdges,
  };
}, [
  diagramState.nodes,
  diagramState.edges,
  diagramState.nodeMap,
  viewBox,
  bounds.maxX,
  bounds.maxY,
  bounds.minX,
  bounds.minY,
  showDependencyEdges,
  showSubTaskEdges,
]);

  /* ========== DEBOUNCED FUNCTIONS ========== */
  const debouncedViewBoxChange = useMemo(
    () =>
      debounce((updater) => {
        setViewBox((prev) => {
          const newBox =
            typeof updater === "function" ? updater(prev) : updater;

          const maxAllowedX = bounds.maxX - newBox.w;
          const maxAllowedY = bounds.maxY - newBox.h;

          const constrainedX = Math.max(
            bounds.minX,
            Math.min(maxAllowedX, newBox.x)
          );
          const constrainedY = Math.max(
            bounds.minY,
            Math.min(maxAllowedY, newBox.y)
          );

          return {
            x: constrainedX,
            y: constrainedY,
            w: Math.max(
              MIN_VIEW_W,
              Math.min(bounds.maxX - bounds.minX, newBox.w)
            ),
            h: Math.max(
              MIN_VIEW_H,
              Math.min(bounds.maxY - bounds.minY, newBox.h)
            ),
          };
        });
      }, BATCH_UPDATE_DELAY),
    [bounds]
  );

  /* ========== VIEWBOX UPDATERS ========== */
  const viewBoxRef = useRef(viewBox);
  useEffect(() => {
    viewBoxRef.current = viewBox;
  }, [viewBox]);

  const immediateViewBoxChange = useCallback((newViewBox) => {
    setViewBox((prev) => {
      const finalBox =
        typeof newViewBox === "function" ? newViewBox(prev) : newViewBox;
      return finalBox;
    });
  }, []);
  useEffect(() => {
    const handleGlobalClick = () => {
      setActiveDropdownNodeId(null);
    };

    document.addEventListener("mousedown", handleGlobalClick);

    return () => {
      document.removeEventListener("mousedown", handleGlobalClick);
    };
  }, []);
  /* ========== CLIENT TO GRAPH COORDINATES ========== */
  const clientToGraph = useCallback(
    (clientX, clientY) => {
      const svg = svgRef.current;
      if (!svg) return { x: clientX, y: clientY };

      const rect = svg.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;

      const graphX = viewBox.x + (px / rect.width) * viewBox.w;
      const graphY = viewBox.y + (py / rect.height) * viewBox.h;

      return { x: graphX, y: graphY };
    },
    [viewBox]
  );

  /* ========== TOGGLE CRITICAL PATH ========== */
  const toggleCriticalPath = useCallback(() => {
    const newShowCriticalOnly = !showCriticalOnly;
    setShowCriticalOnly(newShowCriticalOnly);
    showInfoToast(
      newShowCriticalOnly ? "Critical Path Highlighted" : "Critical Path Hidden"
    );
    setForceResetHover(prev => !prev); // Force reset hover states
  }, [showCriticalOnly]);

  function collapseNodeLineage(nodeId, expandedSet, fullData) {
    const childMap = {};
    fullData.nodes.forEach((n) => {
      if (!childMap[n.parentId]) childMap[n.parentId] = [];
      childMap[n.parentId].push(n.id);
    });

    const nextExpanded = new Set(expandedSet);
    const stack = [nodeId];

    while (stack.length) {
      const cur = stack.pop();
      nextExpanded.delete(cur);
      (childMap[cur] || []).forEach((child) => stack.push(child));
    }

    return nextExpanded;
  }

  /* ========== HIGHLIGHT CHAIN ========== */
  const highlightChain = useCallback(
    async (nodeId, isClearingChain = false) => {
      if (timelineMode && !isClearingChain) {
        showInfoToast("Layered view is disabled in Timeline Mode.");
        return;
      }
      // --- CASE 1: Exit chain view ---

      if (isClearingChain) {
        const clickedNode = fullData.nodeById[nodeId];
        let nextExpanded = new Set(expanded);

        // Always collapse lineage of clicked node IF it has children
        if (clickedNode?.hasChildren) {
          nextExpanded = collapseNodeLineage(nodeId, expanded, fullData);
          setExpanded(nextExpanded);
        }

        // 🔹 EXIT ON ROOT NODE → fully exit layered view
        // 🔹 ROOT EXIT → FULL RESTORE
        if (nodeId === highlightedChain.nodeId) {
          setExitedChainNodes(new Set());
          const restoredExpanded = new Set(preChainExpanded);
          setExpanded(restoredExpanded);

          setHighlightedChain({
            nodeId: null,
            chainNodeIds: new Set(),
            chainEdgeIds: new Set(),
          });

          const subset = buildVisibleSubset(
            fullData,
            restoredExpanded,
            expandDepth
          );

          applyVisibleData(subset);

          if (preChainViewBox) {
            animateViewBox(
              viewBoxRef.current,
              preChainViewBox,
              300,
              (frame) => {
                immediateViewBoxChange(frame);
              }
            );
          }

          showInfoToast("Exited layered view");
          return;
        }
        // 🔹 EXIT ON NON-ROOT NODE → keep layered view ON
        if (nodeId !== highlightedChain.nodeId) {
          setExitedChainNodes((prev) => {
            const next = new Set(prev);
            next.add(nodeId);
            return next;
          });
        }

        const subset = buildVisibleSubset(fullData, nextExpanded, expandDepth);

        // Focus stays correct
        applyVisibleData(subset, nodeId);

        showInfoToast(
          nodeId === highlightedChain.nodeId
            ? "Exited layered view"
            : clickedNode?.hasChildren
              ? `Collapsed lineage for "${clickedNode.title}"`
              : "No children to collapse"
        );

        return;
      }

      // Enter chain view
      const currentViewBox = viewBoxRef.current;
      setPreChainExpanded(new Set(expanded));
      setPreChainViewBox(currentViewBox);

      let currentFullData = fullData;
      const node = currentFullData.nodeById[nodeId];

      if (node && node.hasChildren && !isProjectFullyLoaded) {
        setLoadingNodes(prev => new Set(prev).add(nodeId));
        setLoading(true); // Global loader
        try {
          const res = await Service.EnoviaService().getTaskChildren(nodeId, 999);
          const data = res.data?.data;
          const taskObj = Array.isArray(data) ? data[0] : data;
          let tasks = [];
          if (taskObj) {
            tasks = (Array.isArray(taskObj.children) && taskObj.children.length > 0 ? taskObj.children : null)
              || (Array.isArray(taskObj.relateddata?.tasks) && taskObj.relateddata.tasks.length > 0 ? taskObj.relateddata.tasks : null)
              || (Array.isArray(taskObj.relateddata?.children) && taskObj.relateddata.children.length > 0 ? taskObj.relateddata.children : null)
              || [];
          }
          if (tasks.length === 0 && Array.isArray(data) && data.length > 1) {
            tasks = data.slice(1);
          }
          if (tasks.length > 0) {
            const newNodes = [];
            const newNodeById = {};
            const newEdges = [];
            processTasks(tasks, nodeId, newNodes, newNodeById, newEdges, (node.hierarchyDepth || 0) + 1);

            const existingNodeIds = new Set(currentFullData.nodes.map(n => n.id));
            const filteredNewNodes = newNodes.filter(n => !existingNodeIds.has(n.id));
            const filteredNewEdges = newEdges.filter(e => !currentFullData.edges.some(ce => ce.from === e.from && ce.to === e.to));

            currentFullData = {
              nodes: [...currentFullData.nodes, ...filteredNewNodes],
              edges: [...currentFullData.edges, ...filteredNewEdges],
              nodeById: { ...currentFullData.nodeById, ...newNodeById },
            };
            setFullData(currentFullData);
          }
        } catch (err) {
          console.error("Deep fetch for highlight chain failed:", err);
          showErrorToast("Failed to fetch full chain");
        } finally {
          setLoadingNodes(prev => {
            const s = new Set(prev);
            s.delete(nodeId);
            return s;
          });
          setLoading(false); // Hide global loader
        }
      }

      const { chainNodeIds, chainEdgeIds } = computeHierarchyChain(
        nodeId,
        currentFullData.nodeById
      );

      const newExpanded = new Set(expanded);
      chainNodeIds.forEach((id) => {
        const n = currentFullData.nodeById[id];
        if (n?.hasChildren) newExpanded.add(id);
      });

      setExpanded(newExpanded);
      setHighlightedChain({ nodeId, chainNodeIds, chainEdgeIds });

      const subset = buildVisibleSubset(currentFullData, newExpanded, expandDepth);
      applyVisibleData(subset, nodeId);

      const nodeTitle = node?.title || nodeId;

      const newlyExpandedCount = [...chainNodeIds].filter(
        (id) => !preChainExpanded.has(id) && currentFullData.nodeById[id]?.hasChildren
      ).length;

      showSuccessToast(
        `Elevated lineage chain for "${nodeTitle}" • ${newlyExpandedCount} nodes auto-expanded`
      );
      setForceResetHover(prev => !prev); // Force reset hover states
    },
    [
      expanded,
      fullData,
      expandDepth,
      highlightedChain.nodeId,
      preChainExpanded,
      preChainViewBox,
      timelineMode,
    ]
  );

  /* ========== LOAD PROJECT ========== */
  const loadProject = useCallback(
    (projectId) => {
      setLoading(true);
      initialDone.current = false;

      let cleanProjectId = String(projectId || "").trim();
      if (cleanProjectId.startsWith("<") && cleanProjectId.endsWith(">")) {
        cleanProjectId = cleanProjectId.substring(1, cleanProjectId.length - 1);
      }

      lastProjectIdRef.current = cleanProjectId;

      setExpanded(new Set());
      expandedRef.current = new Set();

      setHighlightedChain({
        nodeId: null,
        chainNodeIds: new Set(),
        chainEdgeIds: new Set(),
      });
      setPreChainExpanded(new Set());
      setIsProjectFullyLoaded(false);
      setShowCriticalOnly(false);
      setTooltip(null);
      setAssigneeTooltip(null);
      setIsAllExpanded(false);
      clearAssigneeCache();

      const isTeamProj = data?.objectType === "Team Project" ||
        data?.objectType === "Project" ||
        data?.objectType === "dsplan:Project" ||
        cleanProjectId.includes("uuid:");

      const loadPromise = isTeamProj
        ? loadTeamProject(cleanProjectId)
        : Service.EnoviaService()
          .getProjectData(cleanProjectId)
          .then((res) => {
            console.log("PROJECT API JSON DATA:", res.data);
            return convertEnoviaToDiagram(res.data);
          });

      loadPromise
        .then((converted) => {
          // If this is a Team Project, pre-populate deliverablesCache to bypass Enovia API requests
          if (isTeamProj && converted.nodes) {
            converted.nodes.forEach((node) => {
              if (node.deliverables && node.deliverables.length > 0) {
                deliverablesCache.set(node.id, node.deliverables);
              } else if (node.id) {
                // Mark empty deliverables explicitly so DeliverablesDots renders immediately without loading
                deliverablesCache.set(node.id, []);
              }
            });
          }

          if (!converted.nodes || converted.nodes.length === 0) {
            setFullData({ nodes: [], edges: [], nodeById: {} });
            dispatch({
              type: "SET_DIAGRAM_DATA",
              payload: {
                nodes: [],
                edges: [],
                nodeMap: {},
                edgeControls: {},
              },
            });
            setLoading(false);
            setTimeout(
              () => showSuccessToast("Project Loaded (No task data)."),
              200
            );
            setForceResetHover(prev => !prev);
            return;
          }

          setFullData(converted);
          if (isTeamProj) {
            setIsProjectFullyLoaded(true);
          }
          const subset = buildVisibleSubset(converted, new Set(), expandDepth);
          applyVisibleData(subset);
          setLoading(false);
          setTimeout(
            () => showSuccessToast("Project Loaded Successfully!"),
            200
          );
          setForceResetHover(prev => !prev); // Force reset hover states
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
          const isUnsupported = err.message && err.message.includes("supported for this view");
          showErrorPopup(
            isUnsupported ? err.message : "Unable to fetch project data. Please verify the project ID and your permissions.",
            isUnsupported ? "Unsupported Object Type" : "Connection Error",
            onFatalError
          );
        });
    },
    [expandDepth, data, onFatalError]
  );

  /* ========== VISIBLE SUBSET BUILDER ========== */
  function buildVisibleSubset(converted, expandedSet, depthLimit) {
    const allNodes = converted.nodes || [];
    const allEdges = converted.edges || [];

    const childrenByParent = {};
    allNodes.forEach((n) => {
      const pid = n.parentId || null;
      (childrenByParent[pid] ??= []).push(n.id);
    });

    const visibleNodeIds = new Set();

    // Add root nodes and mark them
    const rootIds = childrenByParent[null] || [];
    rootIds.forEach((id) => {
      visibleNodeIds.add(id);
    });

    // Expand all nodes that are in the expandedSet recursively
    const stack = [...rootIds];
    stack.forEach(id => visibleNodeIds.add(id));

    const toProcess = [...rootIds];
    while (toProcess.length > 0) {
      const id = toProcess.shift();
      if (expandedSet.has(id)) {
        const children = childrenByParent[id] || [];
        children.forEach(childId => {
          if (!visibleNodeIds.has(childId)) {
            visibleNodeIds.add(childId);
            toProcess.push(childId);
          }
        });
      }
    }

    const visibleNodes = allNodes.filter((n) => visibleNodeIds.has(n.id));

    // Calculate hierarchy depth
    const idToDepth = {};
    function dfs(id, d) {
      idToDepth[id] = d;
      (childrenByParent[id] || []).forEach((childId) => {
        if (visibleNodeIds.has(childId)) dfs(childId, d + 1);
      });
    }
    rootIds.forEach((id) => {
      if (visibleNodeIds.has(id)) dfs(id, 0);
    });

    // Mark root nodes
    visibleNodes.forEach((n) => {
      n.hierarchyDepth = idToDepth[n.id] ?? 0;
      n.isInitial = rootIds.includes(n.id); // Mark as initial if it's a root
    });

    const visibleEdges = allEdges.filter(
      (e) => visibleNodeIds.has(e.from) && visibleNodeIds.has(e.to)
    );

    return {
      nodes: visibleNodes,
      // edges: filterHierarchyOverlap(visibleEdges),
      edges: visibleEdges,
      nodeById: Object.fromEntries(visibleNodes.map((n) => [n.id, n])),
    };
  }

  /* ========== APPLY VISIBLE DATA ========== */
  function applyVisibleData(subset, focusNodeId = null, centerOnFirstWeek = false) {
    const g = buildGraph({ nodes: subset.nodes, edges: subset.edges });
    const levels = computeLevels(g.nodes, g.adj);

    if (timelineMode) {
      assignTimelinePositions(g.nodes, timelineSortBy);
    } else {
      assignPositions(g.nodes, levels);
    }

    // Identify root nodes (nodes with parentId = null or undefined)
    const rootNodeIds = new Set(
      g.nodes.filter(node => !node.parentId).map(node => node.id)
    );

    // Mark nodes with isInitial flag
    g.nodes.forEach((node) => {
      node.isInitial = rootNodeIds.has(node.id); // Mark as initial if it's a root
      node.isRoot = rootNodeIds.has(node.id); // Add another flag if needed
    });

    // Get the new bounds from the positioned nodes BEFORE updating state
    const newBounds = computeNodesBounds(g.nodes);

    // Rest of your code remains the same...
    dispatch({
      type: "SET_DIAGRAM_DATA",
      payload: {
        nodes: g.nodes,
        edges: g.edges,
        nodeMap: Object.fromEntries(g.nodes.map((n) => [n.id, n])),
        edgeControls: Object.fromEntries(g.edges.map((_, i) => [i, undefined])),
      },
    });

    // Use requestAnimationFrame to ensure state is updated before centering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (centerOnFirstWeek) {
          // Immediately snap to the top-left (the first week) without changing zoom
          immediateViewBoxChange({
            x: newBounds.minX,
            y: newBounds.minY,
            w: viewBoxRef.current.w,
            h: viewBoxRef.current.h,
          });
          return;
        }

        // Recenter camera if focusing on a node
        if (focusNodeId) {
          const node = g.nodes.find((n) => n.id === focusNodeId);
          if (node) {
            const currentBox = viewBoxRef.current;
            const target = {
              x: node.x + (node.width || 220) / 2 - currentBox.w / 2,
              y: node.y + (node.height || 85) / 2 - currentBox.h / 2,
              w: currentBox.w,
              h: currentBox.h,
            };

            const maxAllowedX = newBounds.maxX - target.w;
            const maxAllowedY = newBounds.maxY - target.h;

            target.x = Math.max(
              newBounds.minX,
              Math.min(maxAllowedX, target.x)
            );
            target.y = Math.max(
              newBounds.minY,
              Math.min(maxAllowedY, target.y)
            );

            animateViewBox(currentBox, target, 400, (frame) => {
              immediateViewBoxChange(frame);
            });
          }
          return;
        }
        const maxLevel = Math.max(...g.nodes.map((n) => n.level ?? 0));

        // Initial camera setup
        if (
          !initialDone.current &&
          g.nodes.length &&
          maxLevel <= 4 // 👈 only root or root + direct children
        ) {
          initialDone.current = true;

          const b = newBounds;
          const root = g.nodes[0];

          const rootCenterY = root.y + (root.height || 85) / 2;

          const MIN_VISIBLE_H = 300;
          const diagramH = b.maxY - b.minY;

          const h = Math.min(
            viewBoxRef.current.h,
            Math.max(diagramH + 150, MIN_VISIBLE_H)
          );

          const y = rootCenterY - h / 2;

          immediateViewBoxChange({
            x: b.minX + 100, // Removes most of the 120px padding to leave a 20px gap
            y,
            w: viewBoxRef.current.w,
            h,
          });
        } else if (!initialDone.current && g.nodes.length) {
          initialDone.current = true;

          const root = g.nodes[0];

          // Vertical centering on root
          const rootCenterY = root.y + (root.height || 85) / 2;

          // Camera size (do NOT zoom out)
          const w = Math.min(viewBoxRef.current.w, MIN_VIEW_W);
          const h = Math.min(viewBoxRef.current.h, MIN_VIEW_H);

          const newViewBox = {
            x: newBounds.minX + 100, // Removes most of the 120px padding to leave a 20px gap
            y: rootCenterY - h / 2,
            w,
            h,
          };

          // Apply your EXISTING clamp
          const maxAllowedX = newBounds.maxX - newViewBox.w;
          const maxAllowedY = newBounds.maxY - newViewBox.h;

          newViewBox.x = Math.max(
            newBounds.minX,
            Math.min(maxAllowedX, newViewBox.x)
          );
          newViewBox.y = Math.max(
            newBounds.minY,
            Math.min(maxAllowedY, newViewBox.y)
          );

          immediateViewBoxChange(newViewBox);
        }
      });
    });
  }

  /* ========== NODE DOUBLE CLICK ========== */
  const onNodeDoubleClick = useCallback(
    async (nodeId) => {
      if (timelineMode) {
        showInfoToast("Node expansion is disabled in Timeline Mode.");
        return;
      }
      const currentExpanded = new Set(expanded);
      const next = new Set(expanded);

      const node = fullData.nodeById[nodeId];
      if (!node) return;

      if (highlightedChain.nodeId && exitedChainNodes.has(nodeId)) return;

      if (next.has(nodeId)) {
        // Collapse
        next.delete(nodeId);
        setIsAllExpanded(false);
        const childMap = {};
        fullData.nodes.forEach((n) => {
          childMap[n.parentId] ??= [];
          childMap[n.parentId].push(n.id);
        });
        const stack = [nodeId];
        while (stack.length) {
          const cur = stack.pop();
          (childMap[cur] || []).forEach((k) => {
            next.delete(k);
            stack.push(k);
          });
        }
        setExpanded(next);
        const subset = buildVisibleSubset(fullData, next, expandDepth);
        applyVisibleData(subset, nodeId);
        setForceResetHover(prev => !prev);
      } else {
        // === EXPAND PATH ===
        const next = new Set(expanded);

        // Check if children are already in memory (cached from a previous lazy load)
        const hasLoadedChildren = fullData.nodes.some(n => n.parentId === nodeId);
        // Check if we already fetched this node and confirmed it has no children
        const isConfirmedLeaf = fetchedEmptyNodes.has(nodeId);

        if (hasLoadedChildren) {
          // Children already in memory — expand instantly from cache
          setExpandingNode(true);
          setTimeout(() => {
            next.add(nodeId);
            setExpanded(next);
            const subset = buildVisibleSubset(fullData, next, expandDepth);
            const firstCachedChild = fullData.nodes.find(n => n.parentId === nodeId);
            applyVisibleData(subset, firstCachedChild?.id || nodeId);
            setExpandingNode(false);
          }, 100);
        } else if (isConfirmedLeaf || isProjectFullyLoaded) {
          // We already fetched this node or have the full project — do nothing
          showInfoToast(`"${node.title}" has no sub-tasks.`);
        } else {
          // Attempt lazy load — always try unless we know it's a leaf

          // taskLevel preference = max number of levels the user can expand via UI.
          const taskLevel = window?.widget ? parseInt(window.widget.getValue("taskLevel")) || 999 : 999;
          const nodeDepth = node.hierarchyDepth || 0;
          if (nodeDepth >= taskLevel) {
            showInfoToast(`Max depth of ${taskLevel} reached. Increase "Task Level" in widget preferences to expand further.`);
            return;
          }

          setExpandingNode(true);
          setLoadingNodes(prev => new Set(prev).add(nodeId));
          try {
            const res = await Service.EnoviaService().getTaskChildren(nodeId);

            // Task endpoint returns: { data: [{ id, type, children: [...], relateddata: {...} }] }
            // The ENOVIA API puts sub-tasks in EITHER:
            const data = res.data?.data;
            const taskObj = Array.isArray(data) ? data[0] : data;

            let tasks = [];
            if (taskObj) {
              tasks = (Array.isArray(taskObj.children) && taskObj.children.length > 0 ? taskObj.children : null)
                || (Array.isArray(taskObj.relateddata?.tasks) && taskObj.relateddata.tasks.length > 0 ? taskObj.relateddata.tasks : null)
                || (Array.isArray(taskObj.relateddata?.children) && taskObj.relateddata.children.length > 0 ? taskObj.relateddata.children : null)
                || [];
            }

            // Fallback: if data is an array and length > 1, the elements after the first might be children
            if (tasks.length === 0 && Array.isArray(data) && data.length > 1) {
              tasks = data.slice(1);
            }

            if (tasks.length > 0) {
              const newNodes = [];
              const newNodeById = {};
              const newEdges = [];

              processTasks(tasks, nodeId, newNodes, newNodeById, newEdges, (node.hierarchyDepth || 0) + 1);

              const updatedFullData = {
                nodes: [...fullData.nodes, ...newNodes],
                edges: [...fullData.edges, ...newEdges],
                nodeById: { ...fullData.nodeById, ...newNodeById },
              };

              setFullData(updatedFullData);
              next.add(nodeId);
              setExpanded(next);
              const subset = buildVisibleSubset(updatedFullData, next, expandDepth);
              const firstChildId = newNodes[0]?.id || nodeId;
              applyVisibleData(subset, firstChildId);
              showSuccessToast(`Loaded ${tasks.length} sub-task(s) for "${node.title}"`);
            } else {
              // Confirmed leaf — mark so we don't fetch again
              setFetchedEmptyNodes(prev => new Set(prev).add(nodeId));
              showInfoToast(`"${node.title}" has no sub-tasks.`);
            }
          } catch (err) {
            console.error("[LAZY LOAD] Error:", err);
            showErrorToast("Failed to load sub-tasks.");
          } finally {
            setLoadingNodes(prev => {
              const s = new Set(prev);
              s.delete(nodeId);
              return s;
            });
            setExpandingNode(false);
          }
        }
        setForceResetHover(prev => !prev);
      }
    },
    [expanded, fullData, expandDepth, highlightedChain.nodeId, exitedChainNodes, fetchedEmptyNodes, isProjectFullyLoaded, timelineMode, setExpandingNode]
  );

  /* ========== PROMOTE NODE ========== */
  const onPromoteNode = useCallback(
    async (nodeId, taskId, nextState, currentMaturity) => {
      const node = fullData?.nodeById?.[nodeId];
      const isSummary = node?.hasChildren ||
        node?.subtitle === "Project" ||
        node?.subtitle === "Phase" ||
        node?.subtitle === "Summary Task" ||
        node?.subtitle === "dsplan:Project";

      if (isSummary) {
        showErrorToast("Cannot promote summary tasks or project nodes.");
        return;
      }

      setPromotingNode(true);

      const isTeamProj = taskId && (taskId.startsWith("uuid:") || taskId.includes("-"));

      // Skip 'Review' state for Team Projects, transitioning 'In Work' directly to 'Complete'
      let targetState = nextState;
      if (isTeamProj && targetState === "Review") {
        targetState = "Done";
      }

      // Optimistic update
      dispatch({
        type: "UPDATE_NODE",
        payload: { nodeId, newAttributes: { maturity: targetState } }
      });

      try {
        if (isTeamProj) {
          let nextMaturity = targetState;
          if (nextMaturity && !nextMaturity.includes(":")) {
            const lower = nextMaturity.toLowerCase().trim();
            if (lower === "draft" || lower === "create" || lower === "to do" || lower === "todo" || lower === "assign" || lower === "open") {
              nextMaturity = "dsxplan:Open";
            } else if (lower === "in work" || lower === "inprogress" || lower === "active") {
              nextMaturity = "dsxplan:InProgress";
            } else if (lower === "complete" || lower === "completed" || lower === "complted" || lower === "done") {
              nextMaturity = "dsxplan:Done";
            } else {
              const capState = nextMaturity.charAt(0).toUpperCase() + nextMaturity.slice(1);
              nextMaturity = `dsxplan:${capState}`;
            }
          }

          const isMilestone = fullData?.nodeById?.[nodeId]?.subtitle === "Milestone";
          if (isMilestone) {
            await updateTeamMilestone(taskId, { hasMaturity: nextMaturity });
          } else {
            await updateTeamTask(taskId, { hasMaturity: nextMaturity });
          }
        } else {
          await Service.EnoviaService().promoteTask(taskId, nextState);
        }
        showSuccessToast("Task promoted successfully!");
      } catch (err) {
        console.error("Promote failed:", err);
        showErrorToast("Failed to promote task. Reverting state...");
        // Revert optimistic update
        dispatch({
          type: "UPDATE_NODE",
          payload: { nodeId, newAttributes: { maturity: currentMaturity } }
        });
      } finally {
        setPromotingNode(false);
      }
    },
    [fullData]
  );

  /* ========== PAN HANDLERS ========== */
  const onMouseDownSvg = useCallback(
    (e) => {
      if (e.button !== 0 || activeInteraction) return;
      setTooltip(null);
      setAssigneeTooltip(null);

      setActiveInteraction("panning");
      closeAllDropdowns();
      panningRef.current = {
        active: true,
        startClient: { x: e.clientX, y: e.clientY },
        startView: { ...viewBox },
      };

      const moveHandler = (moveEvent) => {
        if (!panningRef.current.active) return;
        const svg = svgRef.current;
        if (!svg) return;

        const rect = svg.getBoundingClientRect();
        const dxPixels = moveEvent.clientX - panningRef.current.startClient.x;
        const dyPixels = moveEvent.clientY - panningRef.current.startClient.y;

        const dxGraph =
          -dxPixels * (panningRef.current.startView.w / rect.width);
        const dyGraph =
          -dyPixels * (panningRef.current.startView.h / rect.height);

        debouncedViewBoxChange({
          ...viewBox,
          x: panningRef.current.startView.x + dxGraph,
          y: panningRef.current.startView.y + dyGraph,
        });
      };

      const upHandler = () => {
        panningRef.current.active = false;
        setActiveInteraction(null);
        document.removeEventListener("mousemove", moveHandler);
        document.removeEventListener("mouseup", upHandler);
      };

      document.addEventListener("mousemove", moveHandler);
      document.addEventListener("mouseup", upHandler);
      e.preventDefault();
    },
    [activeInteraction, viewBox, debouncedViewBoxChange]
  );

  /* ========== WHEEL HANDLING ========== */
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      closeAllDropdowns();

      const svgRect = svgRef.current?.getBoundingClientRect();
      if (!svgRect) return;

      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isZoomAction = e.altKey;
      const isShiftPressed = e.shiftKey;

      const scrollFrame = () => {
        if (isZoomAction) {
          const zoomFactor = 1.1;
          const mouseX = e.clientX - svgRect.left;
          const mouseY = e.clientY - svgRect.top;

          setViewBox((prev) => {
            const graphX = prev.x + (mouseX / svgRect.width) * prev.w;
            const graphY = prev.y + (mouseY / svgRect.height) * prev.h;

            const newW = deltaY > 0 ? prev.w * zoomFactor : prev.w / zoomFactor;
            const newH = deltaY > 0 ? prev.h * zoomFactor : prev.h / zoomFactor;

            const newX = graphX - (mouseX / svgRect.width) * newW;
            const newY = graphY - (mouseY / svgRect.height) * newH;

            const maxAllowedX = bounds.maxX - newW;
            const maxAllowedY = bounds.maxY - newH;

            return {
              x: Math.max(bounds.minX, Math.min(maxAllowedX, newX)),
              y: Math.max(bounds.minY, Math.min(maxAllowedY, newY)),
              w: Math.max(
                MIN_VIEW_W,
                Math.min(bounds.maxX - bounds.minX, newW)
              ),
              h: Math.max(
                MIN_VIEW_H,
                Math.min(bounds.maxY - bounds.minY, newH)
              ),
            };
          });
        } else {
          const scrollSpeed = 0.5;

          setViewBox((prev) => {
            const effectiveDeltaX = isShiftPressed ? deltaY : deltaX;
            const effectiveDeltaY = isShiftPressed ? 0 : deltaY;

            const newX =
              prev.x + effectiveDeltaX * scrollSpeed * (prev.w / svgRect.width);
            const newY =
              prev.y +
              effectiveDeltaY * scrollSpeed * (prev.h / svgRect.height);

            const maxAllowedX = bounds.maxX - prev.w;
            const maxAllowedY = bounds.maxY - prev.h;

            return {
              ...prev,
              x: Math.max(bounds.minX, Math.min(maxAllowedX, newX)),
              y: Math.max(bounds.minY, Math.min(maxAllowedY, newY)),
            };
          });
        }
      };

      requestAnimationFrame(scrollFrame);
    },
    [bounds]
  );

  /* ========== NODE DRAGGING ========== */
  const startNodeDrag = useCallback(
    (nodeId, clientX, clientY) => {
      const node = diagramState.nodeMap[nodeId];
      if (!node) return;

      nodeDragRef.current = {
        active: true,
        nodeId,
        startClient: { x: clientX, y: clientY },
        startNodePos: { x: node.x, y: node.y },
      };

      setActiveInteraction("node");
      document.body.style.userSelect = "none";
    },
    [diagramState.nodeMap]
  );

  const expandAllNodes = useCallback(async () => {
    if (!fullData.nodes.length || !data?.objectId) return;

    if (isProjectFullyLoaded) {
      // Instant expand from cache
      setLoading(true);
      setTimeout(() => {
        const allExpanded = new Set();
        fullData.nodes.forEach((n) => {
          if (n.hasChildren) allExpanded.add(n.id);
        });

        setHighlightedChain({
          nodeId: null,
          chainNodeIds: new Set(),
          chainEdgeIds: new Set(),
        });
        setExitedChainNodes(new Set());

        setExpanded(allExpanded);
        setIsAllExpanded(true);

        const subset = buildVisibleSubset(fullData, allExpanded, expandDepth);
        applyVisibleData(subset);
        setLoading(false);
        showSuccessToast("Expanded all nodes (cached)");
      }, 100);
      return;
    }

    setLoading(true);
    try {
      const res = await Service.EnoviaService().getProjectData(data.objectId, 999);
      const converted = convertEnoviaToDiagram(res.data);

      setFullData(converted);
      setIsProjectFullyLoaded(true);

      // 1️⃣ Expand all nodes that have children
      const allExpanded = new Set();
      converted.nodes.forEach((n) => {
        if (n.hasChildren) {
          allExpanded.add(n.id);
        }
      });

      // 2️⃣ Exit layered view (important to avoid conflicts)
      setHighlightedChain({
        nodeId: null,
        chainNodeIds: new Set(),
        chainEdgeIds: new Set(),
      });
      setExitedChainNodes(new Set());

      setExpanded(allExpanded);
      setIsAllExpanded(true);

      // 3️⃣ Build full visible subset
      const subset = buildVisibleSubset(converted, allExpanded, expandDepth);

      // 4️⃣ Apply layout (no focusNodeId)
      applyVisibleData(subset);

      showSuccessToast("Expanded all nodes");
    } catch (err) {
      console.error("Expand All Failed:", err);
      showErrorToast("Failed to fetch full project data");
    } finally {
      setLoading(false);
    }
  }, [fullData.nodes.length, data, expandDepth, isProjectFullyLoaded]);

  const collapseAllNodes = useCallback(() => {
    if (!fullData.nodes.length) return;

    setHighlightedChain({
      nodeId: null,
      chainNodeIds: new Set(),
      chainEdgeIds: new Set(),
    });
    setExitedChainNodes(new Set());

    setExpanded(new Set());
    setIsAllExpanded(false);
    const subset = buildVisibleSubset(fullData, new Set(), expandDepth);
    applyVisibleData(subset);
    showInfoToast("Collapsed all nodes");
  }, [fullData, expandDepth]);


  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!nodeDragRef.current.active) return;

      const { nodeId, startClient, startNodePos } = nodeDragRef.current;
      const dxPixels = e.clientX - startClient.x;
      const dyPixels = e.clientY - startClient.y;

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const dxGraph = dxPixels * (viewBox.w / rect.width);
      const dyGraph = dyPixels * (viewBox.h / rect.height);

      const newX = startNodePos.x + dxGraph;
      const newY = startNodePos.y + dyGraph;

      dispatch({
        type: "UPDATE_NODES",
        payload: {
          nodes: diagramState.nodes.map((n) =>
            n.id === nodeId ? { ...n, x: newX, y: newY } : n
          ),
        },
      });
    };

    const handleMouseUp = () => {
      nodeDragRef.current.active = false;
      setActiveInteraction(null);
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [diagramState.nodes, viewBox]);

  /* ========== EDGE DRAGGING ========== */
  const onStartEdgeDrag = useCallback(
    (edgeIndex, { clientX, clientY }) => {
      setActiveInteraction("edge");
      const gPos = clientToGraph(clientX, clientY);
      draggingRef.current = {
        dragging: true,
        edgeIndex,
        offsetX: gPos.x,
        offsetY: gPos.y,
      };
      document.body.style.userSelect = "none";
    },
    [clientToGraph]
  );

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current.dragging) return;
      const idx = draggingRef.current.edgeIndex;
      const prev = draggingRef.current;
      const gPos = clientToGraph(e.clientX, e.clientY);
      const dx = gPos.x - prev.offsetX;
      const dy = gPos.y - prev.offsetY;

      draggingRef.current.offsetX = gPos.x;
      draggingRef.current.offsetY = gPos.y;

      dispatch({
        type: "UPDATE_EDGE_CONTROL",
        payload: {
          edgeIndex: idx,
          control: {
            ...diagramState.edgeControls[idx],
            cp1: diagramState.edgeControls[idx]?.cp1
              ? {
                x: diagramState.edgeControls[idx].cp1.x + dx,
                y: diagramState.edgeControls[idx].cp1.y + dy,
              }
              : null,
            cp2: diagramState.edgeControls[idx]?.cp2
              ? {
                x: diagramState.edgeControls[idx].cp2.x + dx,
                y: diagramState.edgeControls[idx].cp2.y + dy,
              }
              : null,
          },
        },
      });
    };

    const up = () => {
      draggingRef.current = {
        dragging: false,
        edgeIndex: null,
        offsetX: 0,
        offsetY: 0,
      };
      setActiveInteraction(null);
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [diagramState.edgeControls, clientToGraph]);

  /* ========== FIT TO SCREEN ========== */
  const fitToScreen = useCallback(() => {
    setFitToScreenLoading(true);
    const b = bounds;
    const padding = 40;
    const paddedWidth = Math.max(100, b.maxX - b.minX + padding * 2);
    const paddedHeight = Math.max(100, b.maxY - b.minY + padding * 2);

    const target = {
      x: b.minX - padding,
      y: b.minY - padding,
      w: paddedWidth,
      h: paddedHeight,
    };

    animateViewBox(viewBoxRef.current, target, 600, (frame) => {
      immediateViewBoxChange(frame);
    }, () => {
      setFitToScreenLoading(false);
      showSuccessToast("Adjusted view to fit all nodes");

      // Set a flag to show a toast once deliverables are done loading
      // We wait a tiny bit to ensure components have had a chance to queue their requests
      setTimeout(() => {
        if (!requestQueue.isIdle()) {
          setIsWaitingForDeliverables(true);
        }
      }, 200);
    });
  }, [bounds, immediateViewBoxChange, debouncedViewBoxChange]);

  const handleExportDiagram = async () => {
    const svg = svgRef.current;
    const nodes = diagramState.nodes;
    if (!svg) {
      showErrorToast("No diagram found to export");
      return;
    }

    setExportLoading(true);

    try {
      const projectName = data?.displayName || data?.objectId || "unknown";
      const b = computeNodesBounds(nodes);
      const diagramWidth = Math.max(1, b.maxX - b.minX);
      const diagramHeight = Math.max(1, b.maxY - b.minY);

      const LARGE_DIAGRAM_AREA = 2000 * 2000;
      const area = diagramWidth * diagramHeight;

      if (area > LARGE_DIAGRAM_AREA) {
        await exportDiagram(
          svg,
          nodes,
          data?.objectId || "unknown",
          computeNodesBounds,
          viewBox,
          projectName
        );
      } else {
        await exportAsPNG();
      }
    } catch (err) {
      console.error("handleExportDiagram failed:", err);
      showErrorToast("Export failed: " + (err?.message || "Unknown"));
    } finally {
      setExportLoading(false);
    }
  };

  /* ========== EXPORT ========== */
  const exportAsPNG = async () => {
    const svg = svgRef.current;
    const nodes = diagramState.nodes;
    if (!svg) {
      showErrorToast("No diagram found to export");
      return;
    }

    setExportLoading(true);

    setTimeout(async () => {
      try {
        const projectName = data?.displayName || data?.objectId || "unknown";
        const fileName = `PertDiagram_${projectName}.png`;

        const b = computeNodesBounds(nodes);
        if (
          !b ||
          b.maxX === undefined ||
          b.minX === undefined ||
          b.maxY === undefined ||
          b.minY === undefined
        ) {
          showErrorToast("Cannot determine diagram bounds");
          return;
        }

        const padding = 20;
        const diagramWidth = Math.max(200, b.maxX - b.minX);
        const diagramHeight = Math.max(200, b.maxY - b.minY);

        if (diagramWidth > 10000 || diagramHeight > 10000) {
          showErrorToast("Diagram is too large to export");
          return;
        }

        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.left = "-9999px";
        tempDiv.style.top = "-9999px";
        tempDiv.style.width = `${diagramWidth + padding * 2}px`;
        tempDiv.style.height = `${diagramHeight + padding * 2}px`;
        tempDiv.style.backgroundColor = "white";
        document.body.appendChild(tempDiv);

        const clonedSvg = svg.cloneNode(true);
        const exportWidth = diagramWidth + padding * 2;
        const exportHeight = diagramHeight + padding * 2;

        clonedSvg.setAttribute("width", exportWidth);
        clonedSvg.setAttribute("height", exportHeight);
        clonedSvg.setAttribute(
          "viewBox",
          `${b.minX - padding} ${b.minY - padding} ${b.maxX - b.minX + padding * 2
          } ${b.maxY - b.minY + padding * 2}`
        );
        clonedSvg.style.backgroundColor = "white";

        const bgRect = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect"
        );
        bgRect.setAttribute("x", b.minX - padding);
        bgRect.setAttribute("y", b.minY - padding);
        bgRect.setAttribute("width", exportWidth);
        bgRect.setAttribute("height", exportHeight);
        bgRect.setAttribute("fill", "white");
        clonedSvg.insertBefore(bgRect, clonedSvg.firstChild);

        tempDiv.appendChild(clonedSvg);

        const canvas = await html2canvas(tempDiv, {
          backgroundColor: "white",
          scale: 2,
          useCORS: true,
          logging: false,
          width: exportWidth,
          height: exportHeight,
        });

        const pngDataUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.download = `PertDiagram_${projectName}.png`;
        link.href = pngDataUrl;
        link.click();

        document.body.removeChild(tempDiv);
        showSuccessToast("Diagram exported successfully!");
      } catch (err) {
        console.error("Export error:", err);
        showErrorToast("Export failed: " + (err.message || "Unknown error"));

        const tempDiv = document.querySelector('[style*="left: -9999px"]');
        if (tempDiv && tempDiv.parentNode === document.body) {
          document.body.removeChild(tempDiv);
        }
      } finally {
        setExportLoading(false);
      }
    }, 10);
  };

  useEffect(() => {
    if (data?.objectId) {
      loadProject(data.objectId);
    }
  }, [data, loadProject]);

  const prevTimelineMode = useRef(timelineMode);

  useEffect(() => {
    if (fullData.nodes.length) {
      const subset = buildVisibleSubset(fullData, expanded, expandDepth);
      const turnedTimelineOn = timelineMode && !prevTimelineMode.current;
      applyVisibleData(subset, null, turnedTimelineOn);
      prevTimelineMode.current = timelineMode;
    }
  }, [timelineMode, timelineSortBy]);

  /* ========== EVENT DELEGATION HANDLER ========== */
  const handleSvgClick = useCallback(
    (e) => {
      let target = e.target;
      while (target && target !== svgRef.current) {
        if (target.dataset?.nodeId) {
          if (e.detail === 2) {
            onNodeDoubleClick(target.dataset.nodeId);
          }
          return;
        }
        if (target.dataset?.edgeIndex) {
          setSelectedEdgeIndex(parseInt(target.dataset.edgeIndex));
          return;
        }
        target = target.parentNode;
      }

      setSelectedEdgeIndex(null);
    },
    [onNodeDoubleClick]
  );

  /* ========== RENDER ========== */
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        position: "relative",
        background: "white",
        overflow: "hidden",
      }}
    >
      {loading && (
        <Loader
          message="Fetching Project Data..."
          size="medium"
          overlay={true}
        />
      )}
      {expandingNode && (
        <Loader
          message="Fetching data..."
          size="medium"
          overlay={true}
        />
      )}
      {promotingNode && (
        <Loader
          message="Promoting task..."
          size="medium"
          overlay={true}
        />
      )}
      {exportLoading && (
        <Loader
          message="Exporting diagram..."
          size="small"
          overlay={true}
          blur={false}
        />
      )}
      {fitToScreenLoading && (
        <Loader
          message="Fitting to screen..."
          size="medium"
          overlay={true}
          blur={true}
        />
      )}
      <ToastNotification />
      {/* Toolbar */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 14,
          zIndex: 2000,
          display: "flex",
          gap: 6,
          background: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderRadius: "10px",
          padding: "2px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        <button
          className="toolbar-btn"
          title="Home"
          onClick={(e) => {
            e.stopPropagation();
            requestQueue.clear();
            if (onHome) onHome();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <Home sx={{ fontSize: 16 }} />
        </button>

        <button
          className="toolbar-btn"
          title="Reload"
          onClick={(e) => {
            e.stopPropagation();
            if (timelineMode) setTimelineMode(false);
            loadProject(data?.objectId);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <Refresh sx={{ fontSize: 16 }} />
        </button>

        <button
          className="toolbar-btn"
          title="Zoom In"
          onClick={() =>
            debouncedViewBoxChange((v) => ({
              ...v,
              w: v.w * 0.9,
              h: v.h * 0.9,
            }))
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <ZoomIn sx={{ fontSize: 16 }} />
        </button>

        <button
          className="toolbar-btn"
          title="Zoom Out"
          onClick={() =>
            debouncedViewBoxChange((v) => ({
              ...v,
              w: v.w * 1.1,
              h: v.h * 1.1,
            }))
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <ZoomOut sx={{ fontSize: 16 }} />
        </button>

        <button
          className="toolbar-btn"
          title="Fit to Screen"
          onClick={fitToScreen}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <FitScreen sx={{ fontSize: 16 }} />
        </button>
        {/* Edge Visibility */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            className="toolbar-btn"
            title="Edge Visibility"
            aria-label="Edge Visibility"
            onClick={(e) => {
              e.stopPropagation();
              if (showEdgeMenu) {
                setShowDependencyEdges(true);
                setShowSubTaskEdges(true);
                setSelectedEdgeIndex(null);
                setHoverEdgeIndex(null);
              }
              setShowEdgeMenu((prev) => !prev);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "6px",
              border: "none",
              background: showEdgeMenu
                ? "rgba(66, 133, 244, 0.15)"
                : "transparent",
              cursor: "pointer",
              color: showEdgeMenu ? "#4285f4" : "#5f6368",
              transition: "all 0.2s ease",
              borderRight: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            <SchemaIcon sx={{ fontSize: 16 }} />
          </button>

          {showEdgeMenu && (
            <select
              value={showDependencyEdges && showSubTaskEdges ? "both" : showDependencyEdges ? "dependency" : "subtask"}
              onChange={(e) => {
                const v = e.target.value;
                setShowDependencyEdges(v === "both" || v === "dependency");
                setShowSubTaskEdges(v === "both" || v === "subtask");
                setSelectedEdgeIndex(null);
                setHoverEdgeIndex(null);
              }}
              style={{
                padding: "2px 4px", fontSize: "12px", borderRadius: "4px",
                border: "1px solid rgba(0,0,0,0.1)", background: "white",
                color: "#333", outline: "none", cursor: "pointer", marginLeft: "4px"
              }}
            >
              <option value="both">All Edges</option>
              <option value="dependency">Dependencies Only</option>
              <option value="subtask">Subtasks Only</option>
            </select>
          )}
        </div>

        {/* Assignee Filter */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
        <button
          className="toolbar-btn"
          title="Filter by Assignee"
          onClick={(e) => {
            e.stopPropagation();
            if (showAssigneeMenu) setSelectedAssignee(null);
            setShowAssigneeMenu(p => !p);
          }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "25px", height: "25px", borderRadius: "6px", border: "none",
            background: selectedAssignee ? "rgba(66,133,244,0.15)" : "transparent",
            cursor: "pointer",
            color: selectedAssignee ? "#4285f4" : "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <PersonIcon sx={{ fontSize: 16 }} />
        </button>
        {showAssigneeMenu && allAssignees.length > 0 && (
          <select
            value={selectedAssignee || ""}
            onChange={e => setSelectedAssignee(e.target.value || null)}
            style={{
              padding: "2px 4px", fontSize: "12px", borderRadius: "4px",
              border: "1px solid rgba(0,0,0,0.1)", background: "white",
              color: "#333", outline: "none", cursor: "pointer", marginLeft: "4px",
              maxWidth: "120px"
            }}
          >
            <option value="">All</option>
            {allAssignees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        </div>
        <button
          className="toolbar-btn"
          title={
            timelineMode
              ? "Critical Path disabled in Timeline Mode"
              : showCriticalOnly
                ? "Hide Critical Path"
                : "Highlight Critical Path"
          }
          onClick={(e) => {
            e.stopPropagation();
            if (timelineMode) return;
            toggleCriticalPath();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: timelineMode
              ? "transparent"
              : showCriticalOnly
                ? "rgba(220, 38, 38, 0.15)"
                : "transparent",
            cursor: timelineMode ? "not-allowed" : "pointer",
            color: timelineMode
              ? "#999"
              : showCriticalOnly
                ? "#dc2626"
                : "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
            opacity: timelineMode ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!timelineMode && !showCriticalOnly) {
              e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
              e.currentTarget.style.color = "#4285f4";
            }
          }}
          onMouseLeave={(e) => {
            if (!timelineMode && !showCriticalOnly) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#5f6368";
            }
          }}
        >
          <TimelineIcon
            sx={{
              fontSize: 16,
              color: timelineMode
                ? "#999"
                : showCriticalOnly
                  ? "#dc2626"
                  : "inherit",
            }}
          />
        </button>

        <button
          className="toolbar-btn"
          title="Export Diagram"
          onClick={handleExportDiagram}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#5f6368",
            transition: "all 0.2s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
            e.currentTarget.style.color = "#4285f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#5f6368";
          }}
        >
          <Download sx={{ fontSize: 16 }} />
        </button>
        {isAllExpanded ? (
          <button
            className="toolbar-btn"
            title={timelineMode ? "Expansion disabled in Timeline Mode" : "Collapse All Nodes"}
            onClick={(e) => {
              e.stopPropagation();
              if (timelineMode) return;
              collapseAllNodes();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "6px",
              border: "none",
              background: timelineMode ? "rgba(0, 0, 0, 0.05)" : "rgba(66, 133, 244, 0.15)",
              cursor: timelineMode ? "not-allowed" : "pointer",
              color: timelineMode ? "#999" : "#4285f4",
              transition: "all 0.2s ease",
              opacity: timelineMode ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!timelineMode) e.currentTarget.style.background = "rgba(66, 133, 244, 0.25)";
            }}
            onMouseLeave={(e) => {
              if (!timelineMode) e.currentTarget.style.background = "rgba(66, 133, 244, 0.15)";
            }}
          >
            <UnfoldLess sx={{ fontSize: 16 }} />
          </button>
        ) : (
          <button
            className="toolbar-btn"
            title={timelineMode ? "Expansion disabled in Timeline Mode" : "Expand All Nodes"}
            onClick={(e) => {
              e.stopPropagation();
              if (timelineMode) return;
              expandAllNodes();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              cursor: timelineMode ? "not-allowed" : "pointer",
              color: timelineMode ? "#999" : "#5f6368",
              transition: "all 0.2s ease",
              opacity: timelineMode ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!timelineMode) {
                e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
                e.currentTarget.style.color = "#4285f4";
              }
            }}
            onMouseLeave={(e) => {
              if (!timelineMode) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#5f6368";
              }
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 16 }} />
          </button>
        )}

        {/* Timeline Toggle */}
        <button
          className="toolbar-btn"
          title={timelineMode ? "Exit Timeline Mode" : "Timeline Mode"}
          onClick={(e) => {
            e.stopPropagation();
            const nextMode = !timelineMode;
            setTimelineMode(nextMode);
            if (nextMode && showCriticalOnly) {
              setShowCriticalOnly(false);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "6px",
            border: "none",
            background: timelineMode
              ? "rgba(66, 133, 244, 0.15)"
              : "transparent",
            cursor: "pointer",
            color: timelineMode ? "#4285f4" : "#5f6368",
            transition: "all 0.2s ease",
            borderRight: timelineMode ? "none" : "1px solid rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            if (!timelineMode) {
              e.currentTarget.style.background = "rgba(66, 133, 244, 0.1)";
              e.currentTarget.style.color = "#4285f4";
            }
          }}
          onMouseLeave={(e) => {
            if (!timelineMode) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#5f6368";
            }
          }}
        >
          <ViewWeek sx={{ fontSize: 16 }} />
        </button>

        {timelineMode && (
          <select
            value={timelineSortBy}
            onChange={(e) => setTimelineSortBy(e.target.value)}
            style={{
              padding: "2px 4px",
              fontSize: "12px",
              borderRadius: "4px",
              border: "1px solid rgba(0,0,0,0.1)",
              background: "white",
              color: "#333",
              outline: "none",
              cursor: "pointer",
              marginLeft: "4px"
            }}
          >
            <option value="startDate">Start Date</option>
            <option value="dueDate">Due Date</option>
          </select>
        )}
      </div>
      {/* Main container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
        onWheel={handleWheel}
      >
        {diagramState.nodes.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              minHeight: "450px",
              background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
              padding: "40px 20px",
              textAlign: "center",
              fontFamily: "System-ui, -apple-system, sans-serif",
            }}
          >
            <div
              style={{
                padding: "40px",
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
                maxWidth: "500px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                transition: "all 0.3s ease",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "24px",
                  boxShadow: "inset 0 2px 4px rgba(255,255,255,0.3)",
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0284C7"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 3v18h18" />
                  <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" style={{ opacity: 0.4 }} />
                  <circle cx="18.5" cy="8.5" r="1.5" fill="#0284C7" />
                  <line x1="12" y1="9" x2="12" y2="15" stroke="#E11D48" strokeWidth="2.5" />
                  <line x1="12" y1="18" x2="12.01" y2="18" stroke="#E11D48" strokeWidth="2.5" />
                </svg>
              </div>

              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#0F172A",
                  margin: "0 0 12px 0",
                }}
              >
                Empty Project Space
              </h3>

              <p
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: "#475569",
                  margin: "0 0 28px 0",
                }}
              >
                This project contains no diagram data (tasks, phases, milestones, or dependencies) to display. Add tasks inside ENOVIA to view your network diagram.
              </p>

              <button
                onClick={onHome}
                style={{
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  background: "linear-gradient(135deg, #0284C7 0%, #0369A1 100%)",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 4px 6px -1px rgba(2, 132, 199, 0.3), 0 2px 4px -1px rgba(2, 132, 199, 0.2)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 12px -1px rgba(2, 132, 199, 0.4), 0 4px 6px -1px rgba(2, 132, 199, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(2, 132, 199, 0.3), 0 2px 4px -1px rgba(2, 132, 199, 0.2)";
                }}
              >
                Back to Drop Zone
              </button>
            </div>
          </div>
        ) : (
          <>
            <ScrollBars
              viewBox={viewBox}
              diagramBounds={bounds}
              onViewBoxChange={debouncedViewBoxChange}
              setActiveInteraction={setActiveInteraction}
            />

            <Minimap
              nodes={diagramState.nodes}
              edges={diagramState.edges}
              diagramBounds={bounds}
              viewBox={viewBox}
              onViewBoxChange={debouncedViewBoxChange}
              setActiveInteraction={setActiveInteraction}
              showCriticalPath={showCriticalOnly}
              highlightedChain={highlightedChain}
              timelineMode={timelineMode}
            />
          </>
        )}

        {/* Single SVG layer with optimized rendering */}
        {diagramState.nodes.length > 0 && (
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
            preserveAspectRatio="xMinYMid meet"
            style={{
              display: "block",
              touchAction: "none",
              userSelect: "none",
              width: "100%",
              height: "100%",
              cursor: activeInteraction === "panning" ? "grabbing" : "grab",
            }}
            onMouseDown={onMouseDownSvg}
            onClick={handleSvgClick}
          >
            <defs>
              <marker
                id="arrow-flow-normal"
                markerWidth="10"
                markerHeight="10"
                refX="8"
                refY="5"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0,0 L0,10 L10,5 z" fill="#333" />
              </marker>

              <marker
                id="arrow-flow-critical"
                markerWidth="10"
                markerHeight="10"
                refX="8"
                refY="5"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0,0 L0,10 L10,5 z" fill="#CC0000" />
              </marker>
              <filter
                id="chainNodeElevation"
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feDropShadow
                  dx="0"
                  dy="4"
                  stdDeviation="8"
                  floodColor="rgb(0,0,0)"
                  floodOpacity="0.3"
                />
                <feDropShadow
                  dx="0"
                  dy="8"
                  stdDeviation="12"
                  floodColor="rgb(0,0,0)"
                  floodOpacity="0.2"
                />
              </filter>

              <filter
                id="criticalNodeGlow"
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="6"
                  floodColor="rgb(204,0,0)"
                  floodOpacity="1"
                />
              </filter>

              <filter id="nodeHover" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="4"
                  floodColor="rgb(66,133,244)"
                  floodOpacity="0.6"
                />
              </filter>

              <filter
                id="chainAndCriticalNode"
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feDropShadow
                  dx="0"
                  dy="4"
                  stdDeviation="8"
                  floodColor="rgb(0,0,0)"
                  floodOpacity="0.3"
                />
                <feDropShadow
                  dx="0"
                  dy="8"
                  stdDeviation="12"
                  floodColor="rgb(0,0,0)"
                  floodOpacity="0.2"
                />
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="6"
                  floodColor="rgb(204,0,0)"
                  floodOpacity="1"
                />
              </filter>
            </defs>

            <g>
              {/* Timeline Column Lines and Headers */}
              {timelineMode && (
                <TimelineHeaders visibleNodes={visibleNodes} bounds={bounds} />
              )}

              {/* Render visible edges with chain highlighting */}
              {!timelineMode && visibleEdges.map((edge, i) => {
                const edgeKey = `${edge.from}=>${edge.to}#${edge.type}`;
                const isChainEdge = highlightedChain.chainEdgeIds.has(edgeKey);
                const isInChain = highlightedChain.nodeId && isChainEdge;
                const edgeOpacity = highlightedChain.nodeId
                  ? isInChain
                    ? 1
                    : 0.15
                  : selectedAssignee
                    ? diagramState.nodeMap[edge.to]?.assignees?.includes(selectedAssignee) ? 1 : 0.1
                  : showCriticalOnly && !edge.isCritical
                    ? 0.4
                    : 1;
                return (
                  <MemoizedEdgeRenderer
                    key={`edge-${i}`}
                    edge={edge}
                    edgeIndex={i}
                    nodeById={diagramState.nodeMap}
                    selectedIndex={selectedEdgeIndex}
                    onSelectEdge={setSelectedEdgeIndex}
                    edgeControls={diagramState.edgeControls}
                    onStartEdgeDrag={
                      highlightedChain.nodeId ? undefined : onStartEdgeDrag
                    }
                    hoverEdgeIndex={hoverEdgeIndex}
                    setHoverEdgeIndex={setHoverEdgeIndex}
                    showCriticalPath={showCriticalOnly}
                    isInChain={isInChain}
                    chainActive={Boolean(highlightedChain.nodeId)}
                    opacity={edgeOpacity}
                    data-edge-index={i}
                    onHoverEnter={
                      highlightedChain.nodeId && !isInChain
                        ? undefined
                        : () => setHoverEdgeIndex(i)
                    }
                    onHoverLeave={
                      highlightedChain.nodeId && !isInChain
                        ? undefined
                        : () => setHoverEdgeIndex(null)
                    }
                  />
                );
              })}

              {/* Render visible nodes with chain highlighting */}
              {visibleNodes.map((node) => {
                const isChainNode = highlightedChain.chainNodeIds.has(node.id);
                const isInChain = highlightedChain.nodeId && isChainNode;
                const isCritical = showCriticalOnly && node.totalFloat === 0;

                const nodeOpacity = highlightedChain.nodeId
                  ? isInChain
                    ? 1
                    : 0.3
                  : selectedAssignee
                    ? node.assignees?.includes(selectedAssignee) ? 1 : 0.3
                  : showCriticalOnly
                    ? isCritical
                      ? 1
                      : 0.3
                    : 1;

                // console.log(`Node ${node.id}:`, {
                //   highlightedChainNodeId: highlightedChain.nodeId,
                //   isChainNode,
                //   isInChain,
                // });
                return (
                  <MemoizedNodeRenderer
                    key={`node-${node.id}`}
                    node={node}
                    isChild={node.hierarchyDepth > 0}
                    onMouseDown={(e) => {
                      if (highlightedChain.nodeId && !isInChain) return;
                      e.stopPropagation();
                      startNodeDrag(node.id, e.clientX, e.clientY);
                    }}
                    onDoubleClick={() => {
                      if (highlightedChain.nodeId && !isInChain) return;
                      onNodeDoubleClick(node.id);
                    }}
                    dragNode={
                      nodeDragRef.current.active
                        ? nodeDragRef.current.nodeId
                        : null
                    }
                    showCriticalPath={showCriticalOnly}
                    activeDropdownNodeId={activeDropdownNodeId}
                    setActiveDropdownNodeId={setActiveDropdownNodeId}
                    projectId={data?.objectId}
                    forceResetHover={forceResetHover || false}
                    loading={loading || loadingNodes.has(node.id)}
                    onHighlightChain={(nodeId, isClearingChain = false) => {
                      highlightChain(nodeId, isClearingChain);
                    }}
                    isInChain={isInChain}
                    isHighlighted={isChainNode}
                    chainRootId={highlightedChain.nodeId}
                    exitedChainNodes={exitedChainNodes}
                    opacity={nodeOpacity}
                    filter={isInChain ? "url(#chainNodeElevation)" : undefined}
                    isExpanded={expanded.has(node.id)}
                    hasChildren={node.hasChildren}
                    // Tooltip handlers
                    onNodeHover={(tooltipInfo) => {
                      if (!highlightedChain.nodeId || isInChain) {
                        setTooltip(tooltipInfo);
                      }
                    }}
                    onNodeLeave={() => setTooltip(null)}
                    onAssigneeHover={(assigneeInfo) => {
                      if (!highlightedChain.nodeId || isInChain) {
                        setAssigneeTooltip(assigneeInfo);
                      }
                    }}
                    onAssigneeLeave={() => setAssigneeTooltip(null)}
                    onHighlightChange={(highlighted) => {
                      // Handle hover highlight if needed
                    }}
                    onRefresh={() => loadProject(data?.objectId)}
                    onUpdateNode={(nodeId, newAttributes) => {
                      dispatch({
                        type: "UPDATE_NODE",
                        payload: { nodeId, newAttributes }
                      });
                    }}
                    onPromoteNode={onPromoteNode}
                    dataNodeId={node.id}
                    onAddDeliverables={(taskId, title) =>
                      setDeliverableTask({ id: taskId, title })
                    }
                    zoomRatio={zoomRatio}
                    timelineMode={timelineMode}
                  />
                );
              })}
            </g>

            {/* Tooltip rendering */}
            {tooltip && (
              <foreignObject
                x={tooltip.x}
                y={tooltip.y}
                width={tooltip.width || 200}
                height={tooltip.height || "auto"}
                style={{
                  pointerEvents: "none",
                  zIndex: 1000,
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    background: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    fontSize: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    whiteSpace: "pre-line",
                    fontFamily: "Arial, sans-serif",
                    maxWidth: "200px",
                    wordWrap: "break-word",
                  }}
                >
                  {tooltip.text}
                </div>
              </foreignObject>
            )}

            {assigneeTooltip && (
              <foreignObject
                x={assigneeTooltip.x}
                y={assigneeTooltip.y}
                width={150}
                height="auto"
                style={{
                  pointerEvents: "none",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "8px",
                    fontSize: "11px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {assigneeTooltip.name}
                  </div>
                  <div style={{ color: "#666" }}>
                    {assigneeTooltip.role || "Assignee"}
                  </div>
                </div>
              </foreignObject>
            )}
          </svg>
        )}

        {/* Deliverable Upload Modal */}
        {deliverableTask && (
          <DeliverableModal
            task={deliverableTask}
            onClose={() => setDeliverableTask(null)}
          />
        )}
      </div>
    </div>
  );
}